import { Injectable } from '@angular/core';
import { map, shareReplay, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { UserPermission } from 'src/app/models/UserPermission';
import { EmployeeService } from 'src/app/services/employee.service';
import { PermissionType } from './PermissionType.enum';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionsCache$: Observable<UserPermission[]>;
  private readonly DEBUG_PREFIX = '[PermissionService]';

  constructor(private mEmployeeService: EmployeeService) {
    console.debug(`${this.DEBUG_PREFIX} Initializing PermissionService`);

    this.permissionsCache$ = this.mEmployeeService.UserRoles$.pipe(
      tap(permissions => {
        console.debug(`${this.DEBUG_PREFIX} Received permissions from UserRoles$:`, {
          permissionsCount: permissions.length,
          permissions: permissions
        });
      }),
      shareReplay(1)
    );
  }

  hasPermission(functionName: string, permissionType: PermissionType): Observable<boolean> {
    console.debug(`${this.DEBUG_PREFIX} Checking permission:`, {
      functionName,
      permissionType
    });

    return this.permissionsCache$.pipe(
      map(permissions => {
        const functionPermission = permissions.find(p => p.functionName === functionName);

        if (!functionPermission) {
          console.warn(`${this.DEBUG_PREFIX} No permission found for function:`, {
            functionName,
            availableFunctions: permissions.map(p => p.functionName)
          });
          return false;
        }

        console.debug(`${this.DEBUG_PREFIX} Found permission for function:`, {
          functionName,
          permissionType,
          permission: functionPermission,
          hasAllPermission: functionPermission.all,
          hasSpecificPermission: functionPermission[permissionType]
        });

        const hasPermission = functionPermission.all || functionPermission[permissionType];

        console.debug(`${this.DEBUG_PREFIX} Permission check result:`, {
          functionName,
          permissionType,
          hasPermission,
          reason: functionPermission.all ? 'ALL permission granted' :
            functionPermission[permissionType] ? `${permissionType} permission granted` :
              'Permission denied'
        });

        return hasPermission;
      })
    );
  }

  hasAnyPermission(functionName: string, permissionTypes: PermissionType[]): Observable<boolean> {
    // console.debug(`${this.DEBUG_PREFIX} Checking multiple permissions:`, {
    //   functionName,
    //   permissionTypes
    // });

    return this.permissionsCache$.pipe(
      map(permissions => {
        const functionPermission = permissions.find(p => p.functionName === functionName);

        if (!functionPermission) {
          console.warn(`${this.DEBUG_PREFIX} No permission found for function:`, {
            functionName,
            availableFunctions: permissions.map(p => p.functionName)
          });
          return false;
        }

        if (functionPermission.all) {
          // console.debug(`${this.DEBUG_PREFIX} ALL permission granted for function:`, {
          //   functionName
          // });
          return true;
        }

        const permissionResults = permissionTypes.map(type => ({
          type,
          granted: functionPermission[type]
        }));

        const hasAnyPermission = permissionTypes.some(type => functionPermission[type]);

        console.debug(`${this.DEBUG_PREFIX} Multiple permissions check result:`, {
          functionName,
          permissionTypes,
          permissionResults,
          hasAnyPermission,
          permission: functionPermission
        });

        return hasAnyPermission;
      })
    );
  }

  getPermissionsByFunction(functionName: string): Observable<UserPermission | null> {
    console.debug(`${this.DEBUG_PREFIX} Getting all permissions for function:`, {
      functionName
    });

    return this.permissionsCache$.pipe(
      map(permissions => {
        const functionPermission = permissions.find(p => p.functionName === functionName);

        console.debug(`${this.DEBUG_PREFIX} Retrieved permissions for function:`, {
          functionName,
          found: !!functionPermission,
          permissions: functionPermission
        });

        return functionPermission || null;
      })
    );
  }

  // Helper method to dump all cached permissions (useful for debugging)
  debugDumpPermissions(): void {
    this.permissionsCache$.subscribe(permissions => {
      console.group(`${this.DEBUG_PREFIX} Current Permissions Cache`);
      console.table(permissions.map(p => ({
        functionName: p.functionName,
        all: p.all,
        add: p.add,
        edit: p.edit,
        archive: p.archive,
        view: p.view,
        excel: p.excel,
        showMenu: p.showMenu,
        group: p.group
      })));
      console.groupEnd();
    });
  }
}
